import { cacheService } from '../services/cacheService';

/**
 * Utility functions to invalidate specific caches when data is updated
 * This helps ensure that the application doesn't show stale data
 */

export const invalidateCache = {
  /**
   * Invalidate all caches
   */
  all: (): void => {
    cacheService.clear();
  },

  /**
   * Invalidate patient-related caches
   * @param patientId Optional patient ID to invalidate specific patient cache
   */
  patients: (patientId?: string): void => {
    if (patientId) {
      // Invalidate specific patient cache
      cacheService.remove(`patient_${patientId}`);
      cacheService.remove(`prior_auths_patient_${patientId}`);
      cacheService.remove(`prescriptions_patient_${patientId}`);
    } else {
      // Invalidate all patient-related caches
      cacheService.remove('patients_all');
      
      // Find and remove all patient-specific caches
      Object.keys(localStorage)
        .filter(key => 
          key.startsWith('cache_patient_') || 
          key.startsWith('cache_prior_auths_patient_') ||
          key.startsWith('cache_prescriptions_patient_')
        )
        .forEach(key => localStorage.removeItem(key));
    }
  },

  /**
   * Invalidate practitioner-related caches
   * @param practitionerId Optional practitioner ID to invalidate specific practitioner cache
   */
  practitioners: (practitionerId?: number): void => {
    if (practitionerId) {
      cacheService.remove(`practitioner_${practitionerId}`);
    } else {
      cacheService.remove('practitioners_all');
      
      // Find and remove all practitioner-specific caches
      Object.keys(localStorage)
        .filter(key => key.startsWith('cache_practitioner_'))
        .forEach(key => localStorage.removeItem(key));
    }
  },

  /**
   * Invalidate prior authorization-related caches
   * @param authId Optional authorization ID
   */
  priorAuths: (authId?: number): void => {
    cacheService.remove('prior_auths_all');
    cacheService.remove('payer_pending_auths');
    cacheService.remove('payer_reviewed_auths');
    
    // Find and remove all prior auth patient-specific caches
    Object.keys(localStorage)
      .filter(key => key.startsWith('cache_prior_auths_patient_'))
      .forEach(key => localStorage.removeItem(key));
  },

  /**
   * Invalidate insurance-related caches
   */
  insurance: (): void => {
    cacheService.remove('insurance_all');
    cacheService.remove('payer_insurance_plans');
  },

  /**
   * Invalidate dashboard-related caches
   */
  dashboard: (): void => {
    cacheService.remove('dashboard_stats');
  }
};